'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { API_ORIGIN as API_BASE_URL } from '@/lib/config';

interface OAuthButtonProps {
  provider: 'google' | 'linkedin' | 'github';
  tenantSlug?: string;
  redirectUrl?: string;
  jobId?: string;
  className?: string;
  variant?: 'default' | 'outline';
  size?: 'default' | 'sm' | 'lg';
  disabled?: boolean;
  children?: React.ReactNode;
}

// Provider-specific configurations
const PROVIDER_CONFIG = {
  google: {
    name: 'Google',
    icon: GoogleIcon,
    // Google brand guidance allows both a light and a dark button. Light surface
    // on light pages; on the dark auth pages (/login, /signup, /apply) switch to
    // Google's dark button colour (#131314) with light text + a subtle border so
    // it stays readable instead of a white slab. The multicolour "G" reads on both.
    bgColor: 'bg-white hover:bg-gray-50 dark:bg-[#131314] dark:hover:bg-[#1f1f20]',
    textColor: 'text-gray-700 dark:text-gray-100',
    borderColor: 'border-gray-300 dark:border-white/20',
  },
  linkedin: {
    name: 'LinkedIn',
    icon: LinkedInIcon,
    bgColor: 'bg-[#0A66C2] hover:bg-[#004182]',
    textColor: 'text-white',
    borderColor: 'border-[#0A66C2]',
  },
  github: {
    name: 'GitHub',
    icon: GitHubIcon,
    bgColor: 'bg-[#24292e] hover:bg-[#1b1f23]',
    textColor: 'text-white',
    borderColor: 'border-[#24292e]',
  },
};

/**
 * OAuth Login Button
 * Redirects to OAuth provider for authentication
 */
export function OAuthButton({
  provider,
  // tenantSlug is extracted from origin in the backend
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tenantSlug: _tenantSlug,
  redirectUrl,
  jobId,
  className,
  variant = 'outline',
  size = 'default',
  disabled = false,
  children,
}: OAuthButtonProps) {
  const config = PROVIDER_CONFIG[provider];
  const Icon = config.icon;

  const handleClick = () => {
    // Build OAuth URL with parameters
    const params = new URLSearchParams();
    
    // Current origin for redirect back
    params.set('origin', window.location.origin);
    
    // Post-auth redirect path
    // Don't redirect back to login/auth pages after successful OAuth
    const currentPath = window.location.pathname;
    const isAuthPage = currentPath.startsWith('/auth/') || currentPath === '/login';
    
    if (redirectUrl) {
      params.set('redirect', redirectUrl);
    } else if (isAuthPage) {
      // Default to dashboard for auth pages
      params.set('redirect', '/me/dashboard');
    } else {
      params.set('redirect', currentPath);
    }
    
    // Job context for apply flow
    if (jobId) {
      params.set('job_id', jobId);
    }
    
    // Redirect to OAuth endpoint
    // API_BASE_URL is API_ORIGIN (without /api) — OAuth uses /auth/* gateway route
    const oauthUrl = `${API_BASE_URL}/auth/oauth/${provider}?${params.toString()}`;
    window.location.href = oauthUrl;
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'w-full flex items-center justify-center gap-3 font-medium',
        variant === 'outline' && [
          config.bgColor,
          config.textColor,
          'border',
          config.borderColor,
        ],
        className
      )}
    >
      <Icon className="h-5 w-5" />
      {children || `Continue with ${config.name}`}
    </Button>
  );
}

/**
 * Google Login Button - Convenience wrapper
 */
export function GoogleLoginButton(props: Omit<OAuthButtonProps, 'provider'>) {
  return <OAuthButton provider="google" {...props} />;
}

/**
 * LinkedIn Login Button - Convenience wrapper
 */
export function LinkedInLoginButton(props: Omit<OAuthButtonProps, 'provider'>) {
  return <OAuthButton provider="linkedin" {...props} />;
}

/**
 * GitHub Login Button - Convenience wrapper (future)
 */
export function GitHubLoginButton(props: Omit<OAuthButtonProps, 'provider'>) {
  return <OAuthButton provider="github" {...props} />;
}

/**
 * OAuth Buttons Group - Show all enabled providers
 */
export function OAuthButtonsGroup({
  providers = ['google'],
  tenantSlug,
  redirectUrl,
  jobId,
  className,
}: {
  providers?: Array<'google' | 'linkedin' | 'github'>;
  tenantSlug?: string;
  redirectUrl?: string;
  jobId?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {providers.map((provider) => (
        <OAuthButton
          key={provider}
          provider={provider}
          tenantSlug={tenantSlug}
          redirectUrl={redirectUrl}
          jobId={jobId}
        />
      ))}
    </div>
  );
}

// ============================================
// Icons
// ============================================

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

function GitHubIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
    </svg>
  );
}
